import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FileText, Send, Bot, FileCheck2, Handshake, Check, Pause, Play } from "lucide-react";
import { SectionHead } from "@/components/public/home/ui";
import { JobPreview, InvitePreview, RoundsPreview, ScorePreview, ShortlistPreview } from "@/components/public/home/workflowPreviews";

const STEPS = [
  { Icon: FileText, t: "Open the job", short: "Job", Preview: JobPreview, status: "Job created",
    d: "Upload a JD and AI fills in the role, must-have skills and knockout questions. Pick the client and the rounds this role needs.",
    pts: ["JD parsed in seconds", "Clients, accounts and service lines", "Choose voice, video and/or coding"] },
  { Icon: Send, t: "Invite candidates", short: "Invite", Preview: InvitePreview, status: "Invited",
    d: "Send invites by email and SMS — one at a time or a bulk CSV. Automatic reminders nudge anyone who stalls.",
    pts: ["Bulk CSV import", "Branded email + SMS", "Automatic reminders"] },
  { Icon: Bot, t: "AI runs every round", short: "Interview", Preview: RoundsPreview, status: "Screening",
    d: "The AI voice and video interviewers talk to the candidate, then the proctored coding round runs — 24/7, in their time zone.",
    pts: ["Real conversations, not forms", "Live progress for recruiters", "Unfinished rounds marked Incomplete"] },
  { Icon: FileCheck2, t: "Scorecards arrive", short: "Score", Preview: ScorePreview, status: "Completed",
    d: "AI scores every round and writes highlights, concerns and a recommendation — so candidates arrive ranked.",
    pts: ["Per-round scores", "Strong hire → No hire verdict", "Transcripts and code as evidence"] },
  { Icon: Handshake, t: "Shortlist to client", short: "Shortlist", Preview: ShortlistPreview, status: "Hired",
    d: "Share branded PDF reports with the hiring manager, mark the hire and see the whole pipeline in analytics.",
    pts: ["Client-ready PDF reports", "One-click sharing", "Funnel and recruiter analytics"] },
];
const TICK = 5000;

function Track({ active, onPick }) {
  const pct = (active / (STEPS.length - 1)) * 100;
  return (
    <div className="relative mt-12 sm:mt-14 px-2 sm:px-6" data-testid="workflow-track">
      <div className="absolute left-[calc(10%)] right-[calc(10%)] top-[58px] sm:top-[66px] h-1 rounded-full bg-slate-200">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-500" animate={{ width: `${pct}%` }} transition={{ duration: 0.6, ease: "easeInOut" }} />
      </div>
      <ol className="relative grid grid-cols-5">
        {STEPS.map(({ Icon, short }, i) => {
          const done = i < active, on = i === active;
          return (
            <li key={short} className="flex flex-col items-center">
              <div className="h-8 sm:h-9 flex items-end">
                {on && (
                  <motion.div layoutId="wf-candidate" transition={{ type: "spring", stiffness: 260, damping: 28 }} data-testid="workflow-candidate"
                    className="flex items-center gap-1.5 rounded-full bg-slate-900 text-white pl-0.5 pr-2.5 py-0.5 shadow-lg whitespace-nowrap">
                    <span className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-400 to-cyan-400 grid place-items-center text-[10px] font-bold">PR</span>
                    <span className="hidden sm:inline text-[11px] font-medium">{STEPS[i].status}</span>
                  </motion.div>
                )}
              </div>
              <button onClick={() => onPick(i)} data-testid={`workflow-step-${i + 1}`} aria-current={on}
                className={`mt-2 w-10 h-10 sm:w-12 sm:h-12 rounded-full grid place-items-center border-2 transition-colors duration-300 ${on ? "bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-500/30" : done ? "bg-white border-indigo-500 text-indigo-600" : "bg-white border-slate-200 text-slate-400 hover:border-slate-400"}`}>
                {done ? <Check className="w-5 h-5" /> : <Icon className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
              <button onClick={() => onPick(i)} className={`mt-2.5 text-[11px] sm:text-sm font-medium transition-colors ${on ? "text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
                <span className="hidden sm:inline font-mono text-slate-400 mr-1">0{i + 1}</span>{short}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function WorkflowTimeline() {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => setActive((a) => (a + 1) % STEPS.length), TICK);
    return () => clearTimeout(t);
  }, [active, playing]);
  const pick = (i) => { setActive(i); setPlaying(false); };
  const s = STEPS[active];

  return (
    <section id="workflow" className="relative bg-white border-t border-slate-100 scroll-mt-16 overflow-hidden" data-testid="workflow-section">
      <div className="absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-indigo-50/60 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <SectionHead eyebrow="How it works" title="Follow one candidate from job description to client shortlist." sub="No scheduling, no note-taking, no copy-pasting between tools. Click any step — or watch Priya move through the pipeline." />
          <button onClick={() => setPlaying((p) => !p)} data-testid="workflow-play-toggle"
            className="shrink-0 self-start sm:self-auto inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 hover:border-slate-400 text-sm font-medium text-slate-700 transition-colors">
            {playing ? <><Pause className="w-4 h-4" /> Pause tour</> : <><Play className="w-4 h-4" /> Play tour</>}
          </button>
        </div>

        <Track active={active} onPick={pick} />

        <AnimatePresence mode="wait">
          <motion.div key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }}
            className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center" data-testid={`workflow-panel-${active + 1}`}>
            <div className="lg:col-span-5 min-w-0">
              <div className="font-mono text-xs text-indigo-600 font-semibold">STEP 0{active + 1} / 05</div>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-slate-900">{s.t}</h3>
              <p className="mt-4 text-slate-600 leading-relaxed">{s.d}</p>
              <ul className="mt-6 space-y-3">
                {s.pts.map((p) => <li key={p} className="flex items-center gap-3 text-sm text-slate-700"><span className="w-5 h-5 rounded-full bg-indigo-50 border border-indigo-100 grid place-items-center"><Check className="w-3 h-3 text-indigo-600" /></span>{p}</li>)}
              </ul>
              {playing && <div className="mt-8 h-1 w-40 rounded-full bg-slate-100 overflow-hidden"><motion.div key={active} className="h-full bg-indigo-500" initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: TICK / 1000, ease: "linear" }} /></div>}
            </div>
            <div className="lg:col-span-7 min-w-0">
              <div className="rounded-3xl bg-gradient-to-br from-indigo-50 via-white to-cyan-50 border border-slate-200 p-4 sm:p-8"><s.Preview /></div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
